/** Export-format surface data for Tier 2 Group F. The runtime
 *  `ExportFormat` union (in `types/image-editor.types.ts`) lists
 *  only formats whose encoders are actually wired (png/jpeg/webp
 *  today). This module lists every format the **dropdown** must
 *  surface — including the formats that #2–#4 of Group F will
 *  wire up (PSD, SVG, AVIF, TIFF, PDF). Surface here is decoupled
 *  from runtime type on purpose: when an encoder lands, the
 *  corresponding entry's `disabled: true` flips to `false` AND the
 *  runtime union widens — both changes happen in the same PR. The
 *  dropdown gating prevents users from picking a format that has
 *  no encoder.
 *
 *  Module is intentionally dependency-free for the same reason as
 *  `export-presets.ts` — keeps the option list importable from any
 *  surface without dragging Fabric or React along. */

import type { ExportFormat } from '../types/image-editor.types';

/** Every option the Format dropdown shows. `value` is the
 *  serialized format identifier (matches `ExportFormat` for wired
 *  entries; the unwired strings are reserved values that will
 *  become valid `ExportFormat` members when their encoder lands).
 *  Order = display order. */
export interface ExportFormatOption {
  value: string;
  label: string;
  /** When true, the option renders disabled in the dropdown and
   *  cannot be the dialog's selected format. */
  disabled: boolean;
  /** Short hint shown next to the label when `disabled` is true.
   *  Empty string when the option is wired. */
  hint: string;
}

/** Built-in format options. Mutate by editing this array.
 *
 *  AVIF is wired in the type system but its `disabled` flag depends
 *  on whether the host browser can encode it — ExportDialog probes
 *  `canvas.toDataURL('image/avif')` at module load and dynamically
 *  re-disables the entry (with a different hint) when feature
 *  detection fails. The flag here is the "type-system-says-wired"
 *  default. */
export const EXPORT_FORMAT_OPTIONS: readonly ExportFormatOption[] = [
  { value: 'png', label: 'PNG (lossless)', disabled: false, hint: '' },
  { value: 'jpeg', label: 'JPEG', disabled: false, hint: '' },
  { value: 'webp', label: 'WebP', disabled: false, hint: '' },
  { value: 'avif', label: 'AVIF', disabled: false, hint: '' },
  { value: 'tiff', label: 'TIFF', disabled: false, hint: '' },
  { value: 'pdf', label: 'PDF', disabled: false, hint: '' },
  { value: 'svg', label: 'SVG', disabled: false, hint: '' },
  { value: 'psd', label: 'PSD', disabled: false, hint: '' },
];

/** Type guard — narrows a dropdown value to the wired runtime
 *  union. The dropdown's `disabled` attribute prevents disabled
 *  options from being selected, but this guard is the runtime
 *  safety net (and lets TypeScript narrow). */
export function isWiredFormat(value: string): value is ExportFormat {
  return (
    value === 'png' ||
    value === 'jpeg' ||
    value === 'webp' ||
    value === 'avif' ||
    value === 'tiff' ||
    value === 'psd' ||
    value === 'svg' ||
    value === 'pdf'
  );
}

/** Lossless raster formats — no quality / compression knob in our
 *  pipeline. PNG ships through the browser's native lossless encoder;
 *  TIFF ships through utif which writes uncompressed RGBA strips
 *  (TIFF tag 259 = 1). Used by the ExportDialog to hide the quality
 *  slider for these formats — matches user expectation that
 *  "lossless" means no quality knob. JPEG / WebP / AVIF stay on
 *  the lossy path (slider visible). */
export function isLosslessRasterFormat(format: ExportFormat): boolean {
  return format === 'png' || format === 'tiff';
}

/** Formats that cannot be written to the OS clipboard. PSD / SVG / PDF
 *  carry structured document state, and TIFF goes through the utif
 *  library encoder — none produce a clipboard-writable raster, so the
 *  ExportDialog hides the "Copy to clipboard" destination for them.
 *
 *  This gates ONLY the clipboard button. PSD / SVG / PDF DO upload to
 *  Fias (the structured-save feature); TIFF is the one format with no
 *  Fias path either, but the Fias-button disable in ExportDialog checks
 *  `format === 'tiff'` directly rather than going through this helper. */
export function isClipboardUnsupportedFormat(format: ExportFormat): boolean {
  return isStructuredFormat(format) || format === 'tiff';
}

/** Structured (non-canvas-native) export formats — formats whose
 *  encoders can't roundtrip through `canvas.toBlob`/`.toDataURL`
 *  because their output is a layered / structured file (PSD / SVG)
 *  or a library-encoded container (PDF). TIFF joins the canvas-
 *  native set (single-raster, just a different encoder) when its
 *  encoder lands; PDF stays here because its file model is more
 *  than a raster.
 *
 *  ExportDialog uses this guard to collapse repeated
 *  `format === 'psd' || format === 'svg'` checks across its
 *  destination gating, quality-slider hiding, and estimate-skip
 *  logic. Pre-PR review finding (R2#6) — earlier versions
 *  enumerated each format inline, which doesn't scale as more
 *  structured formats land. */
export type StructuredExportFormat = Extract<ExportFormat, 'psd' | 'svg' | 'pdf'>;

export function isStructuredFormat(format: ExportFormat): format is StructuredExportFormat {
  return format === 'psd' || format === 'svg' || format === 'pdf';
}

/** Structured formats that cannot be exported per-slice. PSD / SVG
 *  have layered structure that doesn't decompose into independent
 *  per-region files — slicing them produces N copies of the same
 *  document, which is rarely the user's intent. PDF can decompose
 *  (each slice → its own single-page PDF inside the ZIP, mirroring
 *  how PNG / JPEG / WebP slices work), so it is NOT in this set.
 *
 *  Use this guard for slices-region gating instead of
 *  `isStructuredFormat` — the latter still applies to destination
 *  / quality-slider gating, where PDF behaves like PSD / SVG.
 *
 *  Implemented as a positive type guard (the function returns true
 *  for the incompatible variants) so TypeScript narrows the negated
 *  path to the slices-compatible subtype — callers can pass the
 *  narrowed `format` straight into the slice-export pipeline
 *  without a cast. */
export type SlicesIncompatibleFormat = Extract<ExportFormat, 'psd' | 'svg'>;

export function isSlicesIncompatibleFormat(
  format: ExportFormat,
): format is SlicesIncompatibleFormat {
  return format === 'psd' || format === 'svg';
}
